import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { BRAND } from "@/content/landing";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;

export default function MembershipPlans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cfg, setCfg] = useState({});
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/membership-plans`).then((r) => setPlans(r.data || [])).catch(() => {});
    axios.get(`${BACKEND}/api/public/community`).then((r) => setCfg(r.data || {})).catch(() => {});
  }, []);

  const startLoginWith = (target) => {
    const redirect = encodeURIComponent(window.location.origin + target);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  const clickPlan = (plan) => {
    if (plan.payment_type === "custom" || (plan.price_monthly || "").toLowerCase().includes("free") || plan.price_monthly === "RM0") {
      const num = (cfg.whatsapp_number || "+60137003381").replace(/\D/g, "");
      const msg = encodeURIComponent(plan.wa_template || `Hi MIRESCO! I'm interested in ${plan.name}.`);
      window.open(`https://wa.me/${num}?text=${msg}`, "_blank", "noopener");
      return;
    }
    if (!user) return startLoginWith("/membership");
    setSelected(plan);
    setNote("");
  };

  const submitRequest = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post("/payment-requests", {
        plan_id: selected.plan_id,
        plan_name: selected.name,
        amount: selected.price_monthly,
        method: selected.payment_type || "manual_bank",
        reference_note: note.trim(),
      });
      toast.success("Payment request submitted — check WhatsApp for bank details.");
      setSelected(null);
      // deep link to WhatsApp with pre-filled receipt-ready message
      const num = (cfg.whatsapp_number || "+60137003381").replace(/\D/g, "");
      const msg = encodeURIComponent(`Hi MIRESCO! I've submitted a payment request for ${selected.name} (${selected.price_monthly}). Please share bank details so I can transfer.`);
      window.open(`https://wa.me/${num}?text=${msg}`, "_blank", "noopener");
      navigate("/payments");
    } catch (e) {
      toast.error("Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white border border-zinc-300">
            <Sparkles className="w-3.5 h-3.5" style={{ color: RED }} /> Membership Plans
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight leading-[1.05]">
            Pick a plan. <span style={{ color: RED }}>Start earning.</span>
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">
            Manual bank transfer accepted immediately. Card & FPX (Billplz / ToyyibPay) coming soon.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="membership-grid">
        {plans.map((p) => (
          <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`} className="bg-white border border-zinc-200 p-6 hover:border-zinc-900 hover:shadow-[6px_6px_0_0_#09090b] transition-all flex flex-col">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{p.role || "member"}</div>
            <div className="font-display font-black text-xl mt-1 tracking-tight">{p.name}</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display font-black text-3xl" style={{ color: RED }}>{p.price_monthly || "—"}</span>
              {p.price_yearly && <span className="text-xs text-zinc-500">/ {p.price_yearly} yearly</span>}
            </div>
            <ul className="mt-5 space-y-2 flex-1">
              {(p.benefits || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />{b}</li>
              ))}
            </ul>
            <button
              onClick={() => clickPlan(p)}
              data-testid={`plan-cta-${p.plan_id}`}
              className="mt-6 inline-flex items-center justify-center gap-2 text-white px-5 py-3 text-sm font-bold hover:opacity-90"
              style={{ background: p.payment_type === "custom" ? CHARCOAL : RED }}
            >
              {p.cta_label || "Choose"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setSelected(null)} data-testid="payment-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full">
            <div className="p-6 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Confirm request</div>
              <h3 className="font-display font-black text-2xl tracking-tight mt-1">{selected.name}</h3>
              <div className="text-sm mt-1" style={{ color: RED }}>{selected.price_monthly}</div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-zinc-100 border border-zinc-200 p-4 text-sm space-y-1">
                <div className="font-semibold text-zinc-900">Bank transfer details</div>
                <div>{cfg.bank_name || "— Bank name in Admin Settings —"}</div>
                <div>{cfg.bank_account_name || "— Account name in Admin Settings —"}</div>
                <div className="font-mono">{cfg.bank_account_number || "— Account number in Admin Settings —"}</div>
                {cfg.payment_instructions && <div className="text-xs text-zinc-600 mt-2">{cfg.payment_instructions}</div>}
              </div>
              <div>
                <label className="overline text-zinc-500">Note to admin (optional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} rows={3} data-testid="payment-note" placeholder="e.g. Reference number, expected transfer date…" className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none" />
              </div>
            </div>
            <div className="p-6 border-t border-zinc-200 flex items-center justify-end gap-3">
              <button onClick={() => setSelected(null)} className="text-sm font-semibold text-zinc-600 px-4 py-2">Cancel</button>
              <button onClick={submitRequest} disabled={submitting} data-testid="payment-submit" className="bg-[#C8102E] disabled:opacity-50 text-white px-5 py-2 text-sm font-bold">
                {submitting ? "Submitting…" : "Submit & Open WhatsApp"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
